import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Building2,
  Zap,
  ChevronDown,
  ChevronUp,
  Lock,
  ArrowRight,
} from "lucide-react";

/**
 * Conversion-rate trust block for the Fena "Pay by Bank" flow.
 *
 * Sits directly under the green "Secure UK Bank Transfer" panel on the
 * checkout page. Three jobs:
 *   1. Reassure first-time Open Banking users (FCA-regulated, bank-grade,
 *      instant Faster Payments).
 *   2. Demystify the flow with a 4-step expander.
 *   3. Show familiar bank logos so it's visually obvious "this is just my
 *      bank — not a sketchy new payment method".
 *
 * Compliance: nothing therapeutic here, just payment trust signals.
 */
export default function PayByBankTrust() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-4" data-testid="pay-by-bank-trust">
      {/* 3-icon trust strip */}
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
        data-testid="pay-by-bank-trust-strip"
      >
        <TrustIcon
          Icon={Building2}
          title="FCA-regulated"
          body="Authorised by the UK Financial Conduct Authority (firm ref. 928060)."
          testid="trust-icon-fca"
        />
        <TrustIcon
          Icon={ShieldCheck}
          title="Bank-grade security"
          body="You authorise the payment in your own banking app — we never see your card details."
          testid="trust-icon-security"
        />
        <TrustIcon
          Icon={Zap}
          title="Instant Faster Payments"
          body="Transfer settles in seconds, fully traceable, refundable through your bank's protections."
          testid="trust-icon-faster-payments"
        />
      </div>

      {/* "How it works" expander */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-4 w-full flex items-center justify-between gap-3 px-4 py-3
                   border border-[#1E2E44] hover:border-[#00C2D6]/60 rounded-sm
                   bg-[#0B1220]/60 hover:bg-[#0B1220] transition-colors text-left"
        data-testid="how-it-works-toggle"
      >
        <span className="flex items-center gap-2.5">
          <Lock size={14} className="text-[#5EEAD4]" />
          <span className="pg-mono text-[11px] uppercase tracking-[0.18em] text-[#F7FAFC]">
            How Pay by Bank works
          </span>
          <span className="text-[#9CA8B8] text-xs">— 4 quick steps</span>
        </span>
        {open ? (
          <ChevronUp size={16} className="text-[#9CA8B8]" />
        ) : (
          <ChevronDown size={16} className="text-[#9CA8B8]" />
        )}
      </button>

      {open && (
        <div
          className="mt-2 px-5 py-5 border border-[#1E2E44] rounded-sm bg-[#0F172A]/80"
          data-testid="how-it-works-content"
        >
          <ol className="space-y-3">
            <Step n={1} title="Click 'Pay by Bank'">
              You'll see a list of UK banks — pick yours. No sign-up required.
            </Step>
            <Step n={2} title="Your bank's app or website opens">
              We never see your login. You authenticate with your bank as normal —
              Face ID, fingerprint or your usual passcode.
            </Step>
            <Step n={3} title="Confirm the payment in your banking app">
              Your bank shows you the exact amount and recipient (Peptigen Labs Ltd).
              You tap to confirm. The transfer happens instantly via Faster Payments.
            </Step>
            <Step n={4} title="You're back on Peptigen Labs">
              Order confirmation appears immediately. Your bank sends you a receipt
              just like any other Faster Payment.
            </Step>
          </ol>

          <div className="mt-5 pt-4 border-t border-[#1E2E44] flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">
              More questions?
            </span>
            <Link
              to="/help/pay-by-bank"
              className="text-[#5EEAD4] hover:text-[#A6FF00] text-xs inline-flex items-center gap-1"
              data-testid="pay-by-bank-help-link"
            >
              Read the full Pay by Bank guide <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}

      {/* Bank tiles row */}
      <div className="mt-5" data-testid="bank-logos-row">
        <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8] mb-3 text-center">
          Works with every UK current-account bank, including
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {BANKS.map((b) => (
            <BankTile key={b.name} {...b} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** UK retail banks that support Open Banking (Pay by Bank).
 *  Each tile renders the bank's wordmark on its own brand colour — visually
 *  uniform, recognisable, no logo trademark licensing required. */
const BANKS = [
  { name: "Barclays",     bg: "#00AEEF", fg: "#FFFFFF" },
  { name: "HSBC",         bg: "#DB0011", fg: "#FFFFFF" },
  { name: "Lloyds",       bg: "#024731", fg: "#FFFFFF" },
  { name: "NatWest",      bg: "#5A287D", fg: "#FFFFFF" },
  { name: "Santander",    bg: "#EC0000", fg: "#FFFFFF" },
  { name: "Halifax",      bg: "#1B449C", fg: "#FFFFFF" },
  { name: "Nationwide",   bg: "#002F6C", fg: "#FFFFFF" },
  { name: "Monzo",        bg: "#FF4F40", fg: "#FFFFFF" },
  { name: "Starling",     bg: "#7935D2", fg: "#FFFFFF" },
  { name: "Revolut",      bg: "#0666EB", fg: "#FFFFFF" },
  { name: "First Direct", bg: "#101010", fg: "#FFFFFF" },
  { name: "TSB",          bg: "#009BD4", fg: "#FFFFFF" },
];

function BankTile({ name, bg, fg }) {
  return (
    <span
      className="inline-flex items-center justify-center px-3 py-1.5 rounded-[3px] text-[11px]
                 font-bold tracking-[0.02em] shadow-[inset_0_-1px_0_rgba(0,0,0,0.18)]"
      style={{
        backgroundColor: bg,
        color: fg,
        fontFamily:
          "'Helvetica Neue', Helvetica, Arial, sans-serif",
        minWidth: 78,
      }}
      data-testid={`bank-tile-${name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {name}
    </span>
  );
}

function TrustIcon({ Icon, title, body, testid }) {
  return (
    <div
      className="px-4 py-4 rounded-sm border border-[#1E2E44] bg-[#0B1220]/60
                 hover:border-[#5EEAD4]/40 hover:bg-[#0B1220] transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={16} className="text-[#5EEAD4]" />
        <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#F7FAFC]">
          {title}
        </div>
      </div>
      <div className="text-[11.5px] leading-snug text-[#9CA8B8]">{body}</div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-3.5">
      <div
        className="shrink-0 w-7 h-7 rounded-full bg-[#0B1220] border border-[#5EEAD4]/40
                   flex items-center justify-center pg-mono text-[11px] text-[#5EEAD4]"
      >
        {n}
      </div>
      <div className="pt-0.5">
        <div className="text-[#F7FAFC] text-sm font-semibold mb-0.5">{title}</div>
        <div className="text-[#9CA8B8] text-[13px] leading-relaxed">{children}</div>
      </div>
    </li>
  );
}
